import { Link } from '@tanstack/react-router'
import { Trophy } from 'lucide-react'
import { Badge } from '@/shared/components/ui/Badge'
import { Section } from '@/shared/components/ui/Section'
import { Table } from '@/shared/components/ui/Table'
import { useTranslation } from '@/shared/i18n/useTranslation'
import type { TranslateFn } from '@/shared/i18n/useTranslation'
import { SurvivorGameStatus, type SurvivorSeason } from '../types'
import styles from './SurvivorHallOfFame.module.css'

type Props = {
  seasons: SurvivorSeason[]
  /** The season being viewed, so the row for it reads as "you are here" rather than as a link. */
  currentGameId: string
}

/**
 * Every season this game has run, oldest first, with who won it.
 *
 * Each past season is a game of its own on the server, so its row links straight to it — that
 * page still renders the full standings and the round-by-round picks of that year.
 */
export function SurvivorHallOfFame({ seasons, currentGameId }: Props) {
  const { t } = useTranslation()

  if (seasons.length <= 1) return null

  return (
    <Section label={t('survivor.hallOfFame.title')} meta={seasons.length}>
      <Table label={t('survivor.hallOfFame.title')}>
        <tbody>
          {seasons.map((season) => (
            <Table.Row key={season.gameId} isSelf={season.gameId === currentGameId}>
              <Table.Cell isMuted className={styles.seasonCell}>
                {t('survivor.season.short', { number: season.seasonNumber })}
              </Table.Cell>
              <Table.Cell>
                {season.gameId === currentGameId ? (
                  <span className={styles.championNames}>{championLabel(season, t)}</span>
                ) : (
                  <Link
                    to="/sobrevivente/$gameId"
                    params={{ gameId: season.gameId }}
                    search={{ gw: undefined }}
                    className={styles.seasonLink}
                  >
                    {championLabel(season, t)}
                  </Link>
                )}
              </Table.Cell>
              <Table.Cell className={styles.badgeCell}>
                {season.champions.length > 0 ? (
                  <Badge tone={season.iWasChampion ? 'alive' : 'neutral'} variant="soft">
                    <Trophy size={12} aria-hidden="true" />
                    {' '}
                    {season.champions.length > 1
                      ? t('survivor.status.champions')
                      : t('survivor.status.champion')}
                  </Badge>
                ) : (
                  <Badge tone="neutral" variant="outline">{statusLabel(season, t)}</Badge>
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </tbody>
      </Table>
    </Section>
  )
}

/** Champions of a finished season, or the reason a season has none yet. */
function championLabel(season: SurvivorSeason, t: TranslateFn): string {
  if (season.champions.length > 0) {
    return season.champions.map((c) => c.displayName).join(' · ')
  }
  return season.status === SurvivorGameStatus.Finished
    ? t('survivor.hallOfFame.noChampion')
    : t('survivor.hallOfFame.inProgress')
}

function statusLabel(season: SurvivorSeason, t: TranslateFn): string {
  return season.status === SurvivorGameStatus.RegistrationOpen
    ? t('survivor.status.waiting')
    : t('survivor.status.inProgress')
}
